// ABOUTME: Export utility for converting application dashboard data to CSV format
// ABOUTME: Handles both application and offer summary table rows

import { parse as parseObjectToCsv } from 'json2csv';
import { getStatusByConditionType } from 'shared-components';
import { TableRow } from '../../components/organisms/ApplyDataGrid/table/types';
import { getStatusReasonLabel } from '@/utilities/statusReasonLabel';
import { formatDateSafe } from '../dateHelpers';
import { ApplicantOfferStatus, ApplicationStatus } from 'types';
import { systemGeneratedApplicantStatusReasonLabels } from 'data';

export interface ApplicationForExport {
  'Applicant ID': string;
  'Application ID': string;
  'Offer ID': string;
  'First Name': string;
  'Last Name': string;
  Email: string;
  'Intake ID': string;
  'Course ID': string;
  'Course Name': string;
  'Study Mode': string;
  'Entry Year': string;
  Nationality: string;
  'Birth Country': string;
  'Application Country': string;
  'Country of Residence': string;
  'Fee Status': string;
  Status: string;
  'Applied Date': string;
  'Offer Issued Date': string;
  'Offer Type': string;
  'Applicant Response': string;
  'Pending Changes': string;
  'Requirements in Assessment': number;
  'Requirements with Condition': number;
  'Last Updated': string;
  'Stage Change Date': string;
  'New Documents': number;
  'Agent Representation Status': string;
  'Agent Name': string;
  'Student Number': string;
  Tags: string;
  Partners: string;
  'Date of Birth': string;
  'Phone Number': string;
  'UCAS ID': string;
  Gender: string;
  'Study Level': string;
  'Total Scholarships Applied': string;
  'Closed Reason': string;
  'Highest Qualification on Entry': string;
}

export const serializeApplicationForExport = (row: TableRow): ApplicationForExport => {
  // Format tags as a semicolon-separated string
  const tags = row.tags ? row.tags.map(tag => tag.label || '').join('; ') : '';

  // Format partners as a semicolon-separated string
  const partners = row.partners ? row.partners.join('; ') : '';

  // Get course information from programme object or directly from row
  const courseId = row.programme?.courseId || row.courseId || '';
  const courseName = row.programme?.courseName || row.courseName || '';
  const studyMode = row.programme?.mode || '';
  const entryPoint = row.programme?.entryPoint || '';

  return {
    'Student Number': row.studentNo || '',
    'Applicant ID': row.userId || '',
    'Application ID': row.applicationId || '',
    'Offer ID': row.offerId || '',
    'Intake ID': row.intakeId || '',
    Status: row.status || row.applicationStatus || '',
    // Leads have no givenName/familyName until submitted in Identity stage; fall back to knownBy (preferred name)
    'First Name': row.givenName || row.knownBy || '',
    'Last Name': row.familyName || '',
    Email: row.email || '',
    'Phone Number': row.phoneNumber ? `'+${row.phoneNumber}` : '',
    'Fee Status': row.feeStatus || '',
    Nationality: row.nationality || '',
    'Birth Country': row.birthCountry || '',
    'Application Country': row.applicationCountry || '',
    'Country of Residence': row.countryOfResidence || '',
    'Date of Birth': formatDateSafe(row.dob, 'DD MM YYYY') || '',
    Gender: row.gender || '',
    'Agent Name': row.agentName || '',
    'Agent Representation Status': row.agentAccessStatus || '',
    'Study Level': row.studyLevel || '',
    'Course ID': courseId,
    'Course Name': courseName,
    'Study Mode': studyMode || '',
    'Entry Year': entryPoint || '',
    'Applied Date': formatDateSafe(row.appliedDate, 'DD MM YYYY') || '',
    'Offer Issued Date': formatDateSafe(row.issueDate, 'DD MM YYYY') || '',
    'Offer Type': row.conditionType || '',
    'Applicant Response': getStatusByConditionType(row.applicantStatus, row.conditionType) ?? '',
    'Pending Changes': row.pendingChanges ? 'Yes' : 'No',
    'Requirements in Assessment': row.requirementsInAssessment || 0,
    'Requirements with Condition': row.requirementsWithCondition || 0,
    'New Documents': row.newDocumentsCount || 0,
    'Last Updated': row.applicantLastUpdatedAt || '',
    'Stage Change Date': formatDateSafe(row.stageChangeDate, 'DD MM YYYY') || '',
    'UCAS ID': row.ucasId || '',
    'Total Scholarships Applied': row.scholarshipsApplied?.toString() || '',
    'Closed Reason': parseClosedReason(row.status ?? row.applicationStatus, row.statusReason),
    'Highest Qualification on Entry': row.highestQualification || '',
    Tags: tags,
    Partners: partners,
  };
};

const clearCommas = (applicationExport: ApplicationForExport) => {
  Object.keys(applicationExport).forEach(k => {
    const key = k as keyof ApplicationForExport;
    if (typeof applicationExport[key] === 'string') {
      (applicationExport as any)[key] = (applicationExport[key] as string)?.replaceAll(',', ';');
    }
  });
};

export const parseApplicationsToCsvString = (applications: Array<TableRow>): string => {
  const handledApplications: ApplicationForExport[] = applications.map(application => {
    const applicationExport = serializeApplicationForExport(application);
    clearCommas(applicationExport);
    return applicationExport;
  });

  return handledApplications.length ? parseObjectToCsv(handledApplications, { escapedQuote: '', quote: '' }) : '';
};

const CLOSED_STATES: ReadonlyArray<ApplicantOfferStatus | ApplicationStatus> = [
  ApplicantOfferStatus.Rejected,
  ApplicantOfferStatus.Withdrawn,
  ApplicationStatus.Rejected,
  ApplicationStatus.Withdrawn,
  ApplicationStatus.Cancelled,
];

const parseClosedReason = (
  status: ApplicantOfferStatus | ApplicationStatus | undefined,
  statusReason: string | null,
): string => {
  if (!statusReason) return '';
  if (!status || !CLOSED_STATES.includes(status)) return '';
  // Closed Reason is staff-selected. Exclude system-generated reasons (AcceptedByApplicant, etc).
  if (statusReason in systemGeneratedApplicantStatusReasonLabels) return '';
  return getStatusReasonLabel(statusReason);
};
